import 'twin.macro';
import 'styled-components/macro';

import { gql } from '@apollo/client/core';
import { useCallback } from 'react';

import { Icon } from 'component-lib';
import { Rating } from 'component-lib/atoms/rating/Rating';
import { useNotificationContext } from 'core/context/Notifications';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { useTrackReviewRatingGetTrackQuery, useTrackReviewSaveRatingMutation } from 'types/graphql';

export interface TrackReviewRatingProps {
  trackID: string;
  teamSlug: string;
  toThankYouPage: () => void;
  toFeedbackPage: (id: string) => void;
}

gql`
  mutation TrackReviewSaveRating($trackID: String!, $participantID: String!, $score: Int!) {
    createTrackReview(trackID: $trackID, participantID: $participantID, score: $score) {
      id
    }
  }
`;

gql`
  query TrackReviewRatingGetTrack($trackID: String!, $teamSlug: String!) {
    track(trackID: $trackID, teamSlug: $teamSlug) {
      id
      slug
      participant {
        id
      }
    }
  }
`;

export const TrackReviewRating = ({ trackID, teamSlug, toThankYouPage, toFeedbackPage }: TrackReviewRatingProps) => {
  const { dispatch } = useNotificationContext();
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const [saveRating, { loading }] = useTrackReviewSaveRatingMutation();
  const { data, loading: loadingGetTrack } = useTrackReviewRatingGetTrackQuery({
    variables: {
      teamSlug,
      trackID,
    },
  });

  const redirect = useCallback(
    (reviewID: string, score: number) => {
      trackAmplitudeEvent({ name: 'Lab - Feedback - Rated track', data: { rating: score } });
      if (score < 4) {
        toFeedbackPage(reviewID);
      } else {
        toThankYouPage();
      }
    },
    [toFeedbackPage, toThankYouPage, trackAmplitudeEvent],
  );

  const onSubmit = useCallback(
    async (score: number) => {
      let result;
      try {
        result = await saveRating({
          variables: {
            trackID,
            participantID: data?.track?.participant?.id!,
            score,
          },
        });
      } catch (err) {
        dispatch('error', 'Failed to save rating', 'Something went wrong while saving rating, please try again.', 5);
      }
      if (result?.data?.createTrackReview?.id) {
        redirect(result.data.createTrackReview.id, score);
      }
    },
    [saveRating, trackID, dispatch, redirect, data],
  );

  return (
    <div tw="max-w-xs text-white justify-center">
      {loading || loadingGetTrack ? (
        <Icon name="circle-loader" tw="animate-spin text-xl" />
      ) : (
        <>
          <h1 tw="text-center text-4xl leading-snug font-medium mb-3">How much did you enjoy this track?</h1>
          <div tw="flex justify-center">
            <div style={{ width: '275px' }}>
              <Rating onSubmit={onSubmit} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
